import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { usePopoverPosition } from '@/hooks/usePopoverPosition';
import { ensureLanguage } from '@/lib/i18n';
import { Popover } from './Popover';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'fi', name: 'languages.finnish', flag: '🇫🇮' },
  { code: 'en', name: 'languages.english', flag: '🇬🇧' },
  { code: 'sv', name: 'languages.swedish', flag: '🇸🇪' },
];

interface LanguageSelectorProps {
  onLanguageChange?: () => void;
  /** `navbar` inherits the branded portal bar colors. */
  variant?: 'light' | 'dark' | 'navbar';
  /** Compact trigger for tight chrome such as the login page. */
  compact?: boolean;
  /** Lift the menu above sticky chrome. Defaults to the shared popover layer. */
  contentZIndex?: number;
}

type LanguageSelectorVariant = NonNullable<LanguageSelectorProps['variant']>;

function languageTriggerClass(variant: LanguageSelectorVariant, compact: boolean): string {
  if (compact && variant === 'navbar') {
    return 'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-[var(--color-brand-navbar-fg,#ffffff)] hover:bg-[var(--color-brand-navbar-hover)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_oklab,var(--color-brand-navbar-fg,#ffffff)_40%,transparent)]';
  }
  if (compact && variant === 'dark') {
    return 'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-white/90 hover:bg-white/10 transition-colors';
  }
  if (compact) {
    return 'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors';
  }
  if (variant === 'navbar') {
    return 'flex cursor-pointer items-center px-4 py-3.5 min-h-[52px] w-full text-left text-[var(--color-brand-navbar-fg,#ffffff)] hover:bg-[var(--color-brand-navbar-hover)] transition-colors touch-manipulation';
  }
  if (variant === 'dark') {
    return 'flex cursor-pointer items-center px-4 py-3.5 min-h-[52px] text-white hover:bg-dark-500/60 active:bg-dark-500/80 transition-all duration-200 w-full text-left touch-manipulation active:scale-[0.98]';
  }
  return 'flex cursor-pointer items-center px-4 py-2 text-foreground hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors w-full text-left';
}

function languageMenuClass(variant: LanguageSelectorVariant): string {
  if (variant === 'dark') {
    return 'fixed bg-dark-700 rounded-md shadow-lg border border-dark-600';
  }
  return 'fixed bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700';
}

function languageOptionClass(variant: LanguageSelectorVariant, isActive: boolean): string {
  if (variant === 'dark') {
    return `flex cursor-pointer items-center w-full px-4 py-2 text-white hover:bg-dark-600 transition-colors ${
      isActive ? 'bg-dark-600 font-medium' : ''
    }`;
  }
  return `flex cursor-pointer items-center w-full px-4 py-2 text-foreground hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
    isActive ? 'bg-gray-50 dark:bg-gray-700 font-medium' : ''
  }`;
}

export function LanguageSelector({
  onLanguageChange,
  variant = 'light',
  compact = false,
  contentZIndex = 50,
}: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleLanguageChange = (languageCode: string) => {
    void (async () => {
      await ensureLanguage(languageCode);
      await i18n.changeLanguage(languageCode);
      setIsOpen(false);
      onLanguageChange?.();
    })();
  };

  const dropdownPosition = usePopoverPosition({
    open: isOpen,
    triggerRef: buttonRef,
    estimatedHeight: 140,
    widthMode: 'fixed',
    fixedWidth: 192,
    openUpwardMode: 'always',
    repositionOnScroll: true,
    repositionOnResize: true,
  });

  const buttonClasses = languageTriggerClass(variant, compact);
  const dropdownClasses = languageMenuClass(variant);

  return (
    <div className='relative'>
      <button
        type='button'
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className={buttonClasses}
        aria-label={t(currentLanguage.name)}
        aria-expanded={isOpen}
      >
        <Globe className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        <span className={compact ? 'flex items-center text-sm font-medium' : 'flex items-center ml-3 text-base'}>
          <span className='mr-2'>{currentLanguage.flag}</span>
          {t(currentLanguage.name)}
        </span>
      </button>

      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        triggerRef={buttonRef}
        contentRef={dropdownRef}
        modal
        portal
        backdropAriaLabel={t('general.close')}
        contentClassName={dropdownClasses}
        contentStyle={{
          top: dropdownPosition.top != null ? `${dropdownPosition.top}px` : undefined,
          bottom: dropdownPosition.bottom != null ? `${dropdownPosition.bottom}px` : undefined,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
        }}
        contentZIndex={contentZIndex}
      >
        {/* The menu is portaled outside parent menus (e.g. the navbar profile menu); without this
            their outside-click handler unmounts the selector on mousedown and the choice is lost. */}
        <div className='py-1' data-ignore-outside-click>
          {languages.map((language) => (
            <button
              type='button'
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className={languageOptionClass(variant, language.code === i18n.language)}
            >
              <span className='mr-2 text-lg'>{language.flag}</span>
              <span>{t(language.name)}</span>
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
}
